import { Box, Stack, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { isSafari } from 'react-device-detect'
import { IconButton, Icons } from 'utoniq-ui'

import {
  CommentsConnectionFragment,
  Maybe,
  ThreadBoxFragment,
  User,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  BottomSheet,
  CommentList,
  CommentListProps,
  ReplyFormRef,
  ReplyFormValues,
  ThreadDetailBox,
  ThreadDetailBoxProps,
  ThreadDetailBoxSkeleton,
} from '@/components/organisms'
import { NotFound } from '@/components/pages/Errors/NotFound'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter } from '@/core'
import { Template } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  user: Maybe<Pick<User, 'id' | 'formattedAccountId'>>
  thread: Maybe<ThreadBoxFragment>
  comments: Maybe<CommentsConnectionFragment>
  replyToDisplayName?: string
  unreadIndex?: number
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onReply: (values: ReplyFormValues, artistId: string) => Promise<void>
  onLikeOrUnlike: (isLike: boolean, likeCount: number) => Promise<void>
  onDeleteComment: CommentListProps['onDeleteComment']
  onReportUserComment: CommentListProps['onReportUser']
  onBlockUserComment: CommentListProps['onBlockUser']
  onReportUser: ThreadDetailBoxProps['onReportUser']
  onBlockUser: ThreadDetailBoxProps['onBlockUser']
  onReplyToReply: CommentListProps['onReplyToReply']
  onDeleteThread: (id: string) => Promise<void>
  onLikeOrUnlikeComment: (input: {
    isLike: boolean
    commentId: string
    id?: string
    likeCount: number
  }) => Promise<void>
  onRemoveReplyToReply: () => void
  onUpdateArtistThreadMuteStatus: () => Promise<void>
}

export const ThreadDetailTemplate: Template<State, Action> = ({
  state,
  onLikeOrUnlike,
  onReply,
  onFetchMore,
  onDeleteComment,
  onReportUserComment,
  onBlockUserComment,
  onReportUser,
  onBlockUser,
  onReplyToReply,
  onDeleteThread,
  onLikeOrUnlikeComment,
  onRemoveReplyToReply,
  onUpdateArtistThreadMuteStatus,
}) => {
  const { routerNames, generatePath, routerPages } = useRouter()
  const replyFormRef = useRef<ReplyFormRef>(null)
  const bottomSheetRef = useRef<HTMLIonModalElement>(null)
  const commentListRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [hiddenReadMore, setHiddenReadMore] = useState<boolean>(false)

  useEffect(() => {
    setHiddenReadMore(!isNaN(Number(state.unreadIndex)))
  }, [state.unreadIndex])

  /**
   * hidden button unread comment when user scroll to bottom
   */
  useEffect(() => {
    function handleScroll(this: Window, event: Event) {
      const artistThemeProvider = this.document
        .getElementsByClassName('artist-theme-provider')
        .item(0)
      if (!artistThemeProvider) {
        return
      }
      if (
        artistThemeProvider.scrollHeight <=
        artistThemeProvider.clientHeight + this.scrollY + 50
      ) {
        setHiddenReadMore(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const bottomSheet = bottomSheetRef.current
    return () => {
      bottomSheet?.dismiss()
      setOpen(false)
    }
  }, [])

  if (!state.loading && !state.thread) {
    return <NotFound useNextRouter={false} />
  }

  return (
    <ArtistThemeProvider artistId={state.thread?.artistId}>
      <Head title={state.thread?.name || routerNames.ArtistThreadDetail} />

      <BaseLayout
        navigation={{
          type: 'replyForm',
          props: {
            ref: replyFormRef,
            onReply: (values: ReplyFormValues) =>
              onReply(values, state.thread?.artistId ?? ''),
            visibleImage: true,
            userId: state.user?.id ?? '',
            replyToDisplayName: state.replyToDisplayName,
            onRemoveReplyToReply: onRemoveReplyToReply,
          },
        }}
        showScrollBar
        scrollFromBottom={!state.loading}
        header={{
          back: true,
          defaultBackPage: state.thread?.artistId
            ? config.applicationType === 'standalone'
              ? generatePath(routerPages.ArtistTop, {
                  artistId: state.thread?.artistId ?? '#',
                })
              : generatePath(routerPages.ArtistThreads, {
                  artistId: state.thread?.artistId ?? '#',
                })
            : undefined,
          title: (
            <Stack
              flex={1}
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{ mx: 6 }}
              onClick={() => setOpen(true)}
            >
              <Typography
                variant="h5"
                sx={styleSets.lineCamp(1)}
                align="center"
              >
                {state.thread?.name ?? ''}
              </Typography>
              <Icons.Info fontSize="small" sx={{ color: 'grey.600' }} />
            </Stack>
          ),
          right: (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{ ml: -8 }}
            >
              {state.thread?.isAllowNotification ? (
                <IconButton.AudioOn
                  size="small"
                  onClick={onUpdateArtistThreadMuteStatus}
                  sx={{ color: 'grey.600' }}
                />
              ) : (
                <IconButton.AudioOff
                  size="small"
                  onClick={onUpdateArtistThreadMuteStatus}
                  sx={{ color: 'grey.600' }}
                />
              )}
              {/*
                  https://zpl.io/25lq8Bo
                  デザイン上にあるが用途が不明のオプションボタン
                  <IconButton.More
                    size="small"
                    edge="end"
                    sx={{ color: 'grey.600' }}
                  /> */}
            </Stack>
          ),
        }}
      >
        {state.loading || !state.thread ? (
          <ThreadDetailBoxSkeleton />
        ) : (
          !state.hasNextPage && (
            <Box>
              <ThreadDetailBox
                onDeleteThread={onDeleteThread}
                currentUser={state.user}
                thread={state.thread}
                onLikeOrUnlike={onLikeOrUnlike}
                onBlockUser={onBlockUser}
                onReportUser={onReportUser}
                onReply={() => replyFormRef.current?.focus()}
              />
            </Box>
          )
        )}
        <Box sx={{ mt: 3 }} />
        <CommentList
          ref={commentListRef}
          type="threads"
          hiddenHeader
          hiddenNotice
          inverted
          typeId={state.thread?.id}
          commentCount={state.thread?.commentCount}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          comments={state.comments}
          user={state.user}
          onFetchMore={async () => {
            const beforeOffsetHeight = commentListRef.current?.offsetHeight ?? 0
            await onFetchMore()
            /**
             * @hack keep current scroll position
             */
            if (isSafari || getPlatform() === 'ios') {
              setTimeout(() => {
                const afterOffsetHeight =
                  commentListRef.current?.offsetHeight ?? 0
                window.scrollTo({
                  top: afterOffsetHeight - beforeOffsetHeight,
                })
              }, 0)
            }
          }}
          onDeleteComment={onDeleteComment}
          onReportUser={onReportUserComment}
          onBlockUser={onBlockUserComment}
          onReplyToReply={onReplyToReply}
          onLikeOrUnlikeComment={onLikeOrUnlikeComment}
          onClickReply={() => replyFormRef.current?.focus()}
          rootCollectionId={state.thread?.id}
          showReaction
          unreadIndex={state.unreadIndex}
          hiddenReadMore={hiddenReadMore}
          onHiddenReadMore={() => setHiddenReadMore(false)}
        />
      </BaseLayout>

      <BottomSheet
        ref={bottomSheetRef}
        open={open}
        breakpoints={[0, 2 / 3, 1]}
        initialBreakpoint={2 / 3}
        onClose={() => setOpen(false)}
        handle
      >
        <BaseLayout
          header={{
            right: (
              <IconButton.Close
                edge="end"
                size="medium"
                onClick={() => setOpen(false)}
              />
            ),
            sx: { pt: 0 },
          }}
          safeArea={{ pt: 0 }}
        >
          {state.thread && (
            <ThreadDetailBox
              paddingImage={5}
              onDeleteThread={onDeleteThread}
              currentUser={state.user}
              thread={state.thread}
              onLikeOrUnlike={onLikeOrUnlike}
              onBlockUser={onBlockUser}
              onReportUser={onReportUser}
            />
          )}
        </BaseLayout>
      </BottomSheet>
    </ArtistThemeProvider>
  )
}
